import PuzzlePreview from '@/components/game/PuzzlePreview';
import { Modal, PixelIcon, CurrencyDisplay } from '@/components/ui';
import { useGame } from '@/contexts/GameContext';
import { useSound } from '@/contexts/SoundContext';
import { useShop } from '@/contexts/ShopContext';
import { puzzles } from '@/game/puzzles';
import type { PuzzleData } from '@/types/puzzle';
import { getPuzzleGrid, getPuzzleMetadata } from '@/types/puzzle';
import { WORLDS, getWorldLevelIndices } from '@/config/worlds';
import { findNextUncompletedLevel, getLevelCardIndex } from '@/utils/levelSelect';
import NoLivesModal from './NoLivesModal';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { RADIUS, COLOR } from '@/config/designTokens';

interface LevelSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLevelSelect: (level: number) => void;
  currentLevel?: number;
  worldId?: string;
}


const LevelSelectModal: React.FC<LevelSelectModalProps> = ({
  isOpen,
  onClose,
  onLevelSelect,
  currentLevel = 0,
  worldId,
}) => {
  const { gameState } = useGame();
  const { shopState, shopManager } = useShop();
  const { playButtonClick, playButtonHover } = useSound();
  const [activeTab, setActiveTab] = useState<string>('all');
  const [showNoLivesModal, setShowNoLivesModal] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const world = worldId ? WORLDS.find(w => w.id === (worldId as any)) : undefined;
  const worldLevels = useMemo(
    () => (world ? getWorldLevelIndices(world, puzzles.length) : null),
    [world]
  );
  const worldLevelSet = useMemo(
    () => (worldLevels ? new Set(worldLevels) : null),
    [worldLevels]
  );
  
  // Get streak info
  const streakStatus = shopManager?.getDailyRewardStatus() || { streak: 0, canClaim: false };

  // Use real completed levels data from game state
  const completedLevelList = gameState.completedLevels || [];
  const completedLevels = useMemo(
    () => new Set(completedLevelList),
    [completedLevelList]
  );
  const candidateLevels = useMemo(
    () => (worldLevels ? [...worldLevels] : puzzles.map((_, index) => index)),
    [worldLevels]
  );
  const filteredLevels = useMemo(
    () =>
      candidateLevels.filter(levelIndex => {
        const isCompleted = completedLevels.has(levelIndex);
        if (activeTab === 'completed') return isCompleted;
        if (activeTab === 'uncompleted') return !isCompleted;
        return true;
      }),
    [activeTab, candidateLevels, completedLevels]
  );

  // Auto-scroll to next uncompleted level when modal opens
  useEffect(() => {
    if (!isOpen || !gridRef.current || filteredLevels.length === 0) return;

    const nextLevel = findNextUncompletedLevel(candidateLevels, completedLevelList);
    const preferredCardIndex = getLevelCardIndex(filteredLevels, nextLevel);
    const levelCards = gridRef.current.querySelectorAll<HTMLElement>('.level-card');
    const targetCard = levelCards[preferredCardIndex] || levelCards[0];
    if (!targetCard) return;

    const scrollTimer = window.setTimeout(() => {
      targetCard.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);

    return () => window.clearTimeout(scrollTimer);
  }, [isOpen, candidateLevels, completedLevelList, filteredLevels]);

  const calculateDifficulty = (puzzle: PuzzleData) => {
    const puzzleMeta = getPuzzleMetadata(puzzle, 0); // Level index not needed for difficulty calc
    if ('difficulty' in puzzleMeta) {
      // Convert string difficulty to number
      const difficultyMap = { 'easy': 1, 'medium': 2, 'hard': 3, 'expert': 4 };
      return difficultyMap[puzzleMeta.difficulty] || 1;
    }
    
    // Fallback calculation for legacy puzzles
    const puzzleGrid = getPuzzleGrid(puzzle);
    let obstacles = 0;
    let pathLength = 0;

    puzzleGrid.forEach(row => {
      for (const cell of row) {
        if (cell === 'x' || cell === 'l' || cell === 's' || cell === 'n') obstacles++;
        if (cell !== '-') pathLength++;
      }
    });

    const complexity = obstacles * 0.7 + pathLength * 0.3;
    if (complexity < 20) return 1;
    if (complexity < 40) return 2;
    if (complexity < 60) return 3;
    if (complexity < 80) return 4;
    return 5;
  };

  const generateDifficultyBars = (difficulty: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <div
        key={i}
        className={`difficulty-bar ${i < difficulty ? 'active' : ''}`}
        style={{
          width: '8px',
          height: '12px',
          background:
            i < difficulty
              ? difficulty <= 2
                ? COLOR.difficultyEasy
                : difficulty <= 4
                ? COLOR.difficultyMedium
                : COLOR.difficultyHard
              : 'rgba(255, 255, 255, 0.2)',
          border: '1px solid var(--pixel-border)',
          borderRadius: RADIUS.xs,
          display: 'inline-block',
          marginRight: '2px',
        }}
      />
    ));
  };

  const handleLevelSelect = (levelIndex: number) => {
    // Check if player has lives
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }

    const isUnlocked =
      levelIndex === 0 || completedLevels.has(levelIndex - 1) || currentLevel >= levelIndex;
    if (isUnlocked) {
      onLevelSelect(levelIndex);
      // Don't call onClose() here - let the parent handle navigation
    }
  };

  const handleTabChange = (tabId: string) => {
    playButtonClick();
    setActiveTab(tabId);
  };

  const handleLevelCardKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    levelIndex: number,
    isUnlocked: boolean
  ) => {
    if (!isUnlocked) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLevelSelect(levelIndex);
    }
  };

  const tabs = [
    { 
      id: 'all', 
      label: 'All Levels', 
      icon: 'grid', 
      count: worldLevelSet ? worldLevelSet.size : puzzles.length 
    },
    { 
      id: 'completed', 
      label: 'Completed', 
      icon: 'star', 
      count: worldLevelSet
        ? Array.from(worldLevelSet.values()).filter((i) => completedLevels.has(i)).length
        : completedLevels.size
    },
    {
      id: 'uncompleted',
      label: 'New',
      icon: 'play',
      count: (worldLevelSet ? worldLevelSet.size : puzzles.length) - (worldLevelSet
        ? Array.from(worldLevelSet.values()).filter((i) => completedLevels.has(i)).length
        : completedLevels.size),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Level Select"
      subtitle={
        world
          ? `${world.name} • ${worldLevelSet?.size ?? 0} levels`
          : `Choose your adventure • ${puzzles.length} levels available`
      }
      variant="secondary"
      size="large"
      className={`level-select-modal menu-facelift-modal ${world?.className || ''}`}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      showCloseButton={true}
      layoutId="nav-button-levels"
      sourceButtonId="levels"
      headerContent={
        <div className="menu-toolbar">
          <CurrencyDisplay
            coins={shopState.coins}
            gems={shopState.gems}
            lives={shopState.lives}
            streak={streakStatus.streak}
            canClaimStreak={streakStatus.canClaim}
            variant="header"
            showStreak={true}
            animated={true}
          />
          {world && (
            <div className="level-select-world-meta">
              <div className="level-select-world-meta__set">
                <PixelIcon name={world.icon} size={16} />
                <span>{world.enemySetName}</span>
              </div>
              <div className="level-select-world-meta__enemies">
                {world.enemies.map((enemy) => (
                  <span
                    key={enemy.id}
                    className="level-select-world-meta__enemy"
                    style={{ '--enemy-accent': enemy.accent } as CSSProperties}
                    title={`${enemy.name}: ${enemy.role}`}
                  >
                    <PixelIcon name={enemy.icon} size={12} color={enemy.accent} />
                    <span>{enemy.name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      }
    >
      {/* Level Grid */}
      <div className="modal-grid modal-grid--auto-fit" ref={gridRef}>
        {filteredLevels.map(levelIndex => {
          const levelNum = levelIndex + 1;
          const levelLabel = levelIndex === 0 ? 'Tutorial' : `Level ${levelNum}`;
          const isCompleted = completedLevels.has(levelIndex);
          const difficulty = calculateDifficulty(puzzles[levelIndex] as PuzzleData);
          const isUnlocked =
            levelIndex === 0 || completedLevels.has(levelIndex - 1) || currentLevel >= levelIndex;
          const isCurrent = levelIndex === currentLevel;
          const levelStats = gameState.levelStats[levelIndex];

          return (
            <div
              key={levelIndex}
              className={`level-card ${isCompleted ? 'completed' : ''} ${
                !isUnlocked ? 'locked' : ''
              } ${isCurrent ? 'current' : ''}`}
              role="button"
              tabIndex={isUnlocked ? 0 : -1}
              aria-disabled={!isUnlocked}
              aria-label={`${levelLabel}${isUnlocked ? '' : ' locked'}`}
              onClick={() => {
                if (isUnlocked) {
                  handleLevelSelect(levelIndex);
                }
              }}
              onKeyDown={event => handleLevelCardKeyDown(event, levelIndex, isUnlocked)}
              onMouseEnter={() => isUnlocked && playButtonHover()}
            >
              {/* Header */}
              <div className="level-card__header">
                <div className="level-card__title">
                  {isCurrent && <PixelIcon name="play" size={14} color="#4caf50" />}
                  {levelLabel}
                </div>
                <div>
                  {isCompleted ? (
                    <PixelIcon name="star" size={16} color="#ffd600" />
                  ) : isUnlocked ? (
                    <PixelIcon name="play" size={16} color="#2196f3" />
                  ) : (
                    <PixelIcon name="lock" size={16} color={COLOR.disabled} />
                  )}
                </div>
              </div>

              {/* Preview */}
              <div className="level-card__preview">
                <div className="level-card__preview-inner">
                  {puzzles[levelIndex] && <PuzzlePreview levelData={getPuzzleGrid(puzzles[levelIndex] as PuzzleData)} />}
                </div>
              </div>

              {/* Info */}
              <div>
                <div className="level-card__difficulty-row">
                  <span className="level-card__difficulty-label">Difficulty:</span>
                  <div className="difficulty-bars">{generateDifficultyBars(difficulty)}</div>
                </div>

                {/* Level Stats */}
                {levelStats && levelStats.completed && (
                  <div className="level-card__stats">
                    <div className="level-card__stats-row">
                      <span>
                        Best Time: {Math.floor(levelStats.bestTime / 60)}:
                        {(levelStats.bestTime % 60).toString().padStart(2, '0')}
                      </span>
                      <div className="level-card__stars">
        {Array.from({ length: levelStats.stars }, (_, i) => (
          <PixelIcon key={i} name="star" size={10} color="#ffd600" />
        ))}
      </div>
                    </div>
                    <div className="level-card__stats-row">
                      <span>Best Score: {levelStats.bestScore}</span>
                      <span>Attempts: {levelStats.totalAttempts}</span>
                    </div>
                  </div>
                )}

                <button
                  className={`btn-small ${
                    !isUnlocked
                      ? 'btn-secondary disabled'
                      : isCompleted
                      ? 'btn-success'
                      : 'btn-primary'
                  } level-card__action-btn`}
                  disabled={!isUnlocked}
                >
                  <span className="btn-text">
                    {!isUnlocked
                      ? 'Locked'
                      : isCompleted
                      ? 'Play Again'
                      : isCurrent
                      ? 'Continue'
                      : 'Start Level'}
                  </span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredLevels.length === 0 && (
        <div className="level-select-empty-state">
          <div className="level-select-empty-state__icon">
            <PixelIcon name="search" size={32} color="#a0aec0" />
          </div>
          <p>No levels found for "{activeTab}" filter.</p>
        </div>
      )}

      {/* No Lives Modal */}
      <NoLivesModal
        isOpen={showNoLivesModal}
        onClose={() => setShowNoLivesModal(false)}
        onShop={() => {
          setShowNoLivesModal(false);
          onClose(); // Close level select modal
          // Navigate to shop - parent should handle this
          const shopButton = document.querySelector('[data-shop-trigger]');
          if (shopButton instanceof HTMLElement) {
            shopButton.click();
          }
        }}
      />
    </Modal>
  );
};

export default LevelSelectModal;
